/**
 * Named colour tables shared by the avatar builder, the wardrobe UI and the
 * judge prompt generator (which names colours instead of dumping hex codes).
 */
export type NamedColor = { readonly name: string; readonly hex: string };

export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** 12 skin tones, very light to deep. */
export const SKIN_TONES = [
  { name: 'Porcelain', hex: '#F9E4D4' },
  { name: 'Ivory', hex: '#F3D9C4' },
  { name: 'Sand', hex: '#EBC8A9' },
  { name: 'Warm Beige', hex: '#E0B48F' },
  { name: 'Honey', hex: '#D19E73' },
  { name: 'Golden', hex: '#C4895C' },
  { name: 'Caramel', hex: '#B07248' },
  { name: 'Amber', hex: '#9A5E3A' },
  { name: 'Chestnut', hex: '#7F4A2E' },
  { name: 'Mocha', hex: '#663B25' },
  { name: 'Espresso', hex: '#4E2C1B' },
  { name: 'Ebony', hex: '#3A2015' },
] as const satisfies readonly NamedColor[];

/** 14 hair colours: naturals plus a few fashion shades. */
export const HAIR_COLORS = [
  { name: 'Jet Black', hex: '#1B1B1F' },
  { name: 'Soft Black', hex: '#2E2A2B' },
  { name: 'Dark Brown', hex: '#4A3226' },
  { name: 'Chocolate', hex: '#5C3A21' },
  { name: 'Chestnut', hex: '#6B4226' },
  { name: 'Auburn', hex: '#8B3A2A' },
  { name: 'Copper', hex: '#B5562E' },
  { name: 'Caramel', hex: '#A9704A' },
  { name: 'Honey Blonde', hex: '#C89A5B' },
  { name: 'Golden Blonde', hex: '#E3BE7A' },
  { name: 'Platinum', hex: '#EFE6D2' },
  { name: 'Silver', hex: '#B8BCC4' },
  { name: 'Cotton Candy', hex: '#F4B6D2' },
  { name: 'Lavender', hex: '#B9A3E3' },
] as const satisfies readonly NamedColor[];

export const EYE_COLORS = [
  { name: 'Dark Brown', hex: '#3B2314' },
  { name: 'Brown', hex: '#6B4423' },
  { name: 'Hazel', hex: '#8E7037' },
  { name: 'Amber', hex: '#B5802A' },
  { name: 'Green', hex: '#3F8F5A' },
  { name: 'Blue', hex: '#3C7FD1' },
  { name: 'Gray', hex: '#7E8A96' },
  { name: 'Violet', hex: '#7B5AB8' },
] as const satisfies readonly NamedColor[];

/** ~36 clothing colours. Names are used verbatim in judge prompts. */
export const PALETTE = [
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Ivory', hex: '#FFF8E7' },
  { name: 'Black', hex: '#111111' },
  { name: 'Charcoal', hex: '#3A3A3A' },
  { name: 'Slate', hex: '#5F6B7A' },
  { name: 'Silver', hex: '#C0C4CC' },
  { name: 'Navy', hex: '#1F2A5C' },
  { name: 'Royal Blue', hex: '#2F55D4' },
  { name: 'Denim Blue', hex: '#3B5B8C' },
  { name: 'Sky Blue', hex: '#7EC8F5' },
  { name: 'Baby Blue', hex: '#BFE3FF' },
  { name: 'Teal', hex: '#1FA39A' },
  { name: 'Turquoise', hex: '#40E0D0' },
  { name: 'Mint', hex: '#A8E6CF' },
  { name: 'Emerald', hex: '#2E8B57' },
  { name: 'Forest', hex: '#23553A' },
  { name: 'Lime', hex: '#B4E33D' },
  { name: 'Olive', hex: '#7A8B3A' },
  { name: 'Yellow', hex: '#FFD93D' },
  { name: 'Gold', hex: '#D4AF37' },
  { name: 'Mustard', hex: '#E1AD01' },
  { name: 'Peach', hex: '#FFD1B3' },
  { name: 'Orange', hex: '#FF8C42' },
  { name: 'Tangerine', hex: '#FF6F3C' },
  { name: 'Coral', hex: '#FF7F7F' },
  { name: 'Red', hex: '#E63946' },
  { name: 'Cherry', hex: '#B3122E' },
  { name: 'Burgundy', hex: '#6D1F2C' },
  { name: 'Hot Pink', hex: '#FF3E9D' },
  { name: 'Bubblegum', hex: '#FFA1D0' },
  { name: 'Blush', hex: '#F7C6D0' },
  { name: 'Rose', hex: '#D9707D' },
  { name: 'Lavender', hex: '#B9A3E3' },
  { name: 'Violet', hex: '#7B4FD8' },
  { name: 'Plum', hex: '#5A2A6B' },
  { name: 'Brown', hex: '#6B4226' },
  { name: 'Tan', hex: '#C9A27E' },
] as const satisfies readonly NamedColor[];

export const LIP_COLORS = [
  { name: 'Nude', hex: '#C98E7A' },
  { name: 'Rose', hex: '#D9707D' },
  { name: 'Coral', hex: '#FF7F7F' },
  { name: 'Cherry Red', hex: '#C81D3C' },
  { name: 'Berry', hex: '#8E2A5B' },
  { name: 'Plum', hex: '#5A2A6B' },
  { name: 'Hot Pink', hex: '#FF3E9D' },
  { name: 'Gloss Pink', hex: '#F7A1C4' },
] as const satisfies readonly NamedColor[];

export const BLUSH_COLORS = [
  { name: 'Soft Peach', hex: '#FFC2A8' },
  { name: 'Rosy', hex: '#F49AA8' },
  { name: 'Berry', hex: '#D8708F' },
  { name: 'Bronze', hex: '#C98B5A' },
] as const satisfies readonly NamedColor[];

export const EYESHADOW_COLORS = [
  { name: 'Champagne', hex: '#EBD5B3' },
  { name: 'Bronze', hex: '#B07A45' },
  { name: 'Rose Gold', hex: '#E0A199' },
  { name: 'Smoky', hex: '#4B4550' },
  { name: 'Lilac', hex: '#C3A6E6' },
  { name: 'Electric Blue', hex: '#3F7FE6' },
  { name: 'Emerald', hex: '#2E8B57' },
  { name: 'Gold Glitter', hex: '#F2C94C' },
] as const satisfies readonly NamedColor[];

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_RE.test(value);
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = Number.parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Perceptually weighted RGB distance (cheap, good enough for naming). */
export function colorDistance(a: string, b: string): number {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  const rMean = (r1 + r2) / 2;
  const dr = r1 - r2;
  const dg = g1 - g2;
  const db = b1 - b2;
  return Math.sqrt((2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db);
}

export function nearestNamedColor(hex: string, palette: readonly NamedColor[] = PALETTE): NamedColor {
  let best: NamedColor = palette[0] ?? { name: 'Unknown', hex };
  let bestD = Number.POSITIVE_INFINITY;
  if (!isHexColor(hex)) return best;
  for (const c of palette) {
    const d = colorDistance(hex, c.hex);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

/** Human name for any hex, using the closest entry of the given palette. */
export function colorName(hex: string, palette: readonly NamedColor[] = PALETTE): string {
  return nearestNamedColor(hex, palette).name;
}
